import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';
import Button from '../common/Button';
import MovingBorderButton from '../ui/MovingBorderButton';
import { IoCheckmark, IoReorderThreeOutline } from 'react-icons/io5';
import { FiLayers, FiSend, FiShield, FiUsers } from 'react-icons/fi';
import {
  trackFormStart,
  trackFormSubmitAttempt,
  trackFormSubmitFailure,
  trackFormSubmitSuccess,
  trackFormValidationError,
  trackModalClose,
  trackNavClick,
  trackWaitlistFormInvalid,
  trackWaitlistModalOpen,
  trackWaitlistNotifyButtonClick,
  trackWaitlistNotifySubmit,
  trackWaitlistSubmitFailed,
} from '../../utils/analytics';
import { REFF_OPEN_WAITLIST_EVENT, WAITLIST_PENDING_STORAGE_KEY } from '../../utils/waitlistEvents';
import { submitLaunchWaitlistEntry } from '../../services/launchWaitlist';

export type LaunchModalVariant = 'signin' | 'signup';

/** One card inside a nav item's hover mega menu. */
interface NavMenuItem {
  label: string;
  to: string;
  description: string;
  /** Used by the `cards` layout — small circular badge icon. */
  icon?: React.ReactNode;
  /** Used by the `showcase` layout — large illustration panel. */
  visual?: React.ReactNode;
}

/**
 * `cards`    — compact 2×2 grid, icon + text + pill inside each card.
 * `showcase` — two large visual panels with the copy below each one.
 */
type NavMenuVariant = 'cards' | 'showcase';

/** Top-level header nav entry; `menu` opts it into the hover mega menu. */
interface LandingNavItem {
  label: string;
  to: string;
  menu?: readonly NavMenuItem[];
  menuVariant?: NavMenuVariant;
}

/* ── Showcase illustrations (built from divs so they inherit the theme) ── */

/** Stack of referral request rows — represents a job seeker's requests. */
const RequestStackVisual = () => (
  <div className="mm-viz mm-viz--stack" aria-hidden>
    <img
      src="/assets/img/header-svgs/person-wishlist.svg"
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  </div>
);

/** Hub-and-spoke network — represents a professional's reach. */
const NetworkVisual = () => (
  <div className="mm-viz mm-viz--network" aria-hidden>
    <img
      src="/assets/img/header-svgs/group-brainstorming.svg"
      alt=""
      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
    />
  </div>
);

/** Rich success state: structured for animation, social proof, and invite-to-share. */
export interface SubscribeSuccessStory {
  badge: string;
  headline: string;
  subhead: string;
  bodyLines: string[];
  perksTitle: string;
  perks: string[];
  invite: string;
  signOff: string;
  team: string;
}

export interface LaunchNoticeContent {
  title: string;
  message: string;
  formLabel: string;
  inputPlaceholder: string;
  submitLabel: string;
  /** Plain summary for assistive tech (also used if `successStory` is overridden incompletely). */
  successMessage: string;
  invalidMessage: string;
  successStory?: Partial<SubscribeSuccessStory>;
}

export interface LaunchNoticeOverrides {
  signin?: Partial<LaunchNoticeContent>;
  signup?: Partial<LaunchNoticeContent>;
}

/** After `resolveLaunchNotice`, `successStory` is always a full merge of defaults + overrides. */
type ResolvedLaunchNoticeContent = Omit<LaunchNoticeContent, 'successStory'> & {
  successStory: SubscribeSuccessStory;
};

const DEFAULT_SUBSCRIBE_SUCCESS_STORY: SubscribeSuccessStory = {
  badge: 'You are in',
  headline: 'Welcome to Reffconnect',
  subhead: "Thanks for subscribing. You're on our priority launch list.",
  bodyLines: [
    "When we go live, you'll be one of the first people invited in.",
  ],
  perksTitle: 'What you get',
  perks: [
    'First access when we go live',
    'Launch updates in your inbox',
  ],
  invite: 'Know someone who should join? Share Reffconnect with them.',
  signOff: 'Thank you for joining us early.',
  team: 'Team Reffconnect',
};

const REFFCONNECT_WEBSITE_URL = 'https://www.reffconnect.com';
const REFFCONNECT_SHARE_TEXT =
  "I just joined the Reffconnect early access list. Check it out: https://www.reffconnect.com";

function successStoryToPlainText(story: SubscribeSuccessStory): string {
  return [
    story.headline,
    story.subhead,
    ...story.bodyLines,
    story.perksTitle,
    ...story.perks.map((p) => `• ${p}`),
    story.invite,
    `${story.signOff} — ${story.team}`,
  ].join('\n\n');
}

const DEFAULT_LAUNCH_NOTICE: Record<LaunchModalVariant, LaunchNoticeContent> = {
  signin: {
    title: "We're launching soon",
    message:
      'Full sign-in is not live yet. Stay tuned—we will email you the moment your account area opens.',
    formLabel: 'Notify me when sign-in opens',
    inputPlaceholder: 'you@gmail.com',
    submitLabel: 'Notify me',
    successMessage: successStoryToPlainText(DEFAULT_SUBSCRIBE_SUCCESS_STORY),
    invalidMessage: 'Please enter a valid email address.',
  },
  signup: {
    title: "We're launching soon",
    message:
      'Registration opens with our launch. Add your email below and we will let you know as soon as you can create an account.',
    formLabel: 'Join the waitlist',
    inputPlaceholder: 'you@gmail.com',
    submitLabel: 'Join waitlist',
    successMessage: successStoryToPlainText(DEFAULT_SUBSCRIBE_SUCCESS_STORY),
    invalidMessage: 'Please enter a valid email address.',
  },
};

function resolveLaunchNotice(
  variant: LaunchModalVariant,
  overrides?: LaunchNoticeOverrides
): ResolvedLaunchNoticeContent {
  const merged = { ...DEFAULT_LAUNCH_NOTICE[variant], ...overrides?.[variant] };
  const story: SubscribeSuccessStory = {
    ...DEFAULT_SUBSCRIBE_SUCCESS_STORY,
    ...merged.successStory,
  };
  return {
    ...merged,
    successStory: story,
    successMessage: overrides?.[variant]?.successMessage ?? successStoryToPlainText(story),
  };
}

interface HeaderProps {
  showAuthButtons?: boolean;
  /** When true (default): transparent bar at top, frosted bar after scroll — same as home. Use `false` for the compact solid header. */
  showLandingNav?: boolean;
  /** Optional copy overrides per button; merged with built-in defaults. */
  launchNotice?: LaunchNoticeOverrides;
}

const Header: React.FC<HeaderProps> = ({
  showAuthButtons = true,
  showLandingNav = true,
  launchNotice,
}) => {
  const [landingMenuOpen, setLandingMenuOpen] = useState(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState<string | null>(null);
  /** `to` of the nav item whose dropdown is currently open (desktop hover). */
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownCloseTimer = useRef<number | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const [launchModalOpen, setLaunchModalOpen] = useState(false);
  const [launchModalVariant, setLaunchModalVariant] = useState<LaunchModalVariant>('signup');
  const [launchModalAnimKey, setLaunchModalAnimKey] = useState(0);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeCompany, setSubscribeCompany] = useState('');
  /** Empty until user picks an option; professional shows company field. */
  const [waitlistRole, setWaitlistRole] = useState<'' | 'professional' | 'jobseeker'>('');
  const [subscribeFeedback, setSubscribeFeedback] = useState<'idle' | 'invalid' | 'success'>('idle');
  const [subscribeInvalidField, setSubscribeInvalidField] = useState<'email' | 'role' | 'company' | null>(null);
  const [subscribeSubmitting, setSubscribeSubmitting] = useState(false);
  const [subscribeSubmitError, setSubscribeSubmitError] = useState<string | null>(null);
  const [successAnimKey, setSuccessAnimKey] = useState(0);
  const location = useLocation();

  const launchCopy = useMemo(
    () => resolveLaunchNotice(launchModalVariant, launchNotice),
    [launchModalVariant, launchNotice]
  );

  const successStory = launchCopy.successStory;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(REFFCONNECT_SHARE_TEXT)}`;
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
    REFFCONNECT_WEBSITE_URL
  )}`;

  const openLaunchModal = useCallback((variant: LaunchModalVariant) => {
    setLaunchModalVariant(variant);
    setLaunchModalAnimKey((k) => k + 1);
    setSubscribeEmail('');
    setSubscribeCompany('');
    setWaitlistRole('');
    setSubscribeFeedback('idle');
    setSubscribeInvalidField(null);
    setSubscribeSubmitError(null);
    setSubscribeSubmitting(false);
    setLaunchModalOpen(true);
    trackWaitlistModalOpen({ variant });
  }, []);

  const closeLaunchModal = useCallback(() => {
    trackModalClose({ modal_name: 'waitlist_popup', page_path: window.location.pathname });
    setLaunchModalOpen(false);
    setSubscribeEmail('');
    setSubscribeCompany('');
    setWaitlistRole('');
    setSubscribeFeedback('idle');
    setSubscribeInvalidField(null);
    setSubscribeSubmitError(null);
    setSubscribeSubmitting(false);
  }, []);

  useEffect(() => {
    const onOpenWaitlist = (e: Event) => {
      const ce = e as CustomEvent<{ variant?: LaunchModalVariant }>;
      const v = ce.detail?.variant === 'signin' ? 'signin' : 'signup';
      openLaunchModal(v);
    };
    window.addEventListener(REFF_OPEN_WAITLIST_EVENT, onOpenWaitlist);
    return () => window.removeEventListener(REFF_OPEN_WAITLIST_EVENT, onOpenWaitlist);
  }, [openLaunchModal]);

  /** Open modal after e.g. `/login` → home when signup was queued in sessionStorage. */
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(WAITLIST_PENDING_STORAGE_KEY);
      if (raw === 'signin' || raw === 'signup') {
        sessionStorage.removeItem(WAITLIST_PENDING_STORAGE_KEY);
        openLaunchModal(raw);
      }
    } catch {
      /* ignore */
    }
  }, [location.pathname, openLaunchModal]);

  /** Auto-open waitlist popup on first visit (like cookie consent pattern). */
  useEffect(() => {
    if (!showLandingNav) return; // only on marketing pages with the landing header
    try {
      if (window.localStorage.getItem('reffconnect_waitlist_shown_v1') === '1') return;
    } catch {
      return; // private mode / quota
    }

    const timer = window.setTimeout(() => {
      openLaunchModal('signup');
      try {
        window.localStorage.setItem('reffconnect_waitlist_shown_v1', '1');
      } catch {
        /* ignore */
      }
    }, 2500); // 2.5s delay for better UX — lets the page load first

    return () => window.clearTimeout(timer);
  }, [showLandingNav, openLaunchModal]);

  const handleLaunchSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (subscribeSubmitting) return;

    const trimmedEmail = subscribeEmail.trim();
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (!validEmail) {
      setSubscribeFeedback('invalid');
      setSubscribeInvalidField('email');
      trackFormValidationError({
        form_name: 'waitlist_popup',
        field_name: 'email',
        message: launchCopy.invalidMessage,
        page_path: window.location.pathname,
      });
      trackWaitlistFormInvalid({ variant: launchModalVariant, field: 'email' });
      return;
    }

    if (!waitlistRole) {
      setSubscribeFeedback('invalid');
      setSubscribeInvalidField('role');
      trackFormValidationError({
        form_name: 'waitlist_popup',
        field_name: 'role',
        message: 'Role not selected',
        page_path: window.location.pathname,
      });
      trackWaitlistFormInvalid({ variant: launchModalVariant, field: 'role' });
      return;
    }

    if (waitlistRole === 'professional' && subscribeCompany.trim() === '') {
      setSubscribeFeedback('invalid');
      setSubscribeInvalidField('company');
      trackFormValidationError({
        form_name: 'waitlist_popup',
        field_name: 'company',
        message: 'Company name is required for working professionals',
        page_path: window.location.pathname,
      });
      trackWaitlistFormInvalid({ variant: launchModalVariant, field: 'company' });
      return;
    }

    setSubscribeInvalidField(null);
    setSubscribeSubmitError(null);
    setSubscribeSubmitting(true);
    trackFormSubmitAttempt({ form_name: 'waitlist_popup', page_path: window.location.pathname });

    const audienceRole =
      waitlistRole === 'professional' ? 'working_professional' : 'fresher';
    const companyName =
      waitlistRole === 'professional' && subscribeCompany.trim() !== ''
        ? subscribeCompany.trim()
        : null;

    try {
      const { error, duplicate } = await submitLaunchWaitlistEntry({
        email: trimmedEmail,
        audienceRole,
        companyName,
        openedFrom: launchModalVariant,
      });

      if (error) {
        const msg = error.message || 'Something went wrong. Please try again.';
        setSubscribeSubmitError(msg);
        trackFormSubmitFailure({ form_name: 'waitlist_popup', page_path: window.location.pathname, message: msg });
        trackWaitlistSubmitFailed({ variant: launchModalVariant, error_message: msg });
        return;
      }

      if (duplicate) {
        const msg = 'This email ID already exists in our waitlist.';
        setSubscribeSubmitError(msg);
        trackWaitlistSubmitFailed({ variant: launchModalVariant, error_message: msg });
        return;
      }

      trackWaitlistNotifySubmit({
        variant: launchModalVariant,
        audience_role: audienceRole,
        has_company: Boolean(companyName),
      });
      trackFormSubmitSuccess({ form_name: 'waitlist_popup', page_path: window.location.pathname });
      setSubscribeFeedback('success');
      setSuccessAnimKey((k) => k + 1);
    } catch {
      const msg = 'Something went wrong. Please try again.';
      setSubscribeSubmitError(msg);
      trackFormSubmitFailure({ form_name: 'waitlist_popup', page_path: window.location.pathname, message: msg });
      trackWaitlistSubmitFailed({ variant: launchModalVariant, error_message: msg });
    } finally {
      setSubscribeSubmitting(false);
    }
  };

  useEffect(() => {
    if (!launchModalOpen) return;
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeLaunchModal();
    };
    window.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [launchModalOpen, closeLaunchModal]);

  useEffect(() => {
    if (!showLandingNav) {
      setHasUserScrolled(false);
      setScrolled(false);
      return;
    }

    setHasUserScrolled(false);
    setScrolled(false);

    const onScroll = () => {
      setHasUserScrolled(true);
      setScrolled(window.scrollY > 24);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [showLandingNav, location.pathname]);

  /** Cards shown in the Services hover dropdown. */
  const servicesMenu: readonly NavMenuItem[] = [
    {
      label: 'Resume Review',
      to: '/resume-review',
      description: 'Get actionable feedback from professionals who see real hiring pipelines.',
      icon: <img src="/assets/img/header-svgs/resume-review.svg" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />,
    },
    {
      label: 'Mock Interview',
      to: '/mock-interview',
      description: 'Practice with verified interviewers and get scored feedback to improve.',
      icon: <img src="/assets/img/header-svgs/mock-intriview.svg" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />,
    },
    {
      label: 'Refer Session',
      to: '/refer-session',
      description: 'Connect with insiders for a structured referral conversation and submission.',
      icon: <img src="/assets/img/header-svgs/refferal.svg" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />,
    },
    {
      label: 'Weekly Referral Drop',
      to: '/week-refer-drop',
      description: 'Exclusive weekly referral slots from verified employees at top companies.',
      icon: <img src="/assets/img/header-svgs/weekely drop.svg" alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />,
    },
  ];

  /** Two audience panels shown in the "Who's It For" hover dropdown. */
  const audienceMenu: readonly NavMenuItem[] = [
    {
      label: 'Job Seekers',
      to: '/for-job-seekers',
      description:
        'Skip the application black hole. Send structured referral requests and land interviews faster.',
      visual: <RequestStackVisual />,
    },
    {
      label: 'Working Professionals',
      to: '/for-professionals',
      description:
        'Refer strong candidates from your network, build your reputation, and earn as you help others.',
      visual: <NetworkVisual />,
    },
  ];

  const landingNavItems: readonly LandingNavItem[] = [
    { label: 'What We Offer', to: '/services', menu: servicesMenu, menuVariant: 'cards' },
    { label: "Who's It For", to: '/services', menu: audienceMenu, menuVariant: 'showcase' },
    { label: 'Our Mission', to: '/our-mission' },
    { label: 'About Us', to: '/about' },
    { label: 'Case Study', to: '/case-study' },
    { label: 'Contact Us', to: '/contact-us' },
  ];

  /** Options for the currently hovered nav item, if it has a dropdown. */
  const openNavItem = landingNavItems.find((item) => item.label === openDropdown);
  const openMenu = openNavItem?.menu;
  const openMenuVariant: NavMenuVariant = openNavItem?.menuVariant ?? 'cards';

  /** Open immediately; cancel any pending close so moving link → card keeps it open. */
  const openNavDropdown = useCallback((key: string) => {
    if (dropdownCloseTimer.current !== null) {
      window.clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
    setOpenDropdown(key);
  }, []);

  /** Small grace period so the cursor can cross the gap between trigger and card. */
  const scheduleCloseNavDropdown = useCallback(() => {
    if (dropdownCloseTimer.current !== null) {
      window.clearTimeout(dropdownCloseTimer.current);
    }
    dropdownCloseTimer.current = window.setTimeout(() => {
      setOpenDropdown(null);
      dropdownCloseTimer.current = null;
    }, 140);
  }, []);

  const closeNavDropdownNow = useCallback(() => {
    if (dropdownCloseTimer.current !== null) {
      window.clearTimeout(dropdownCloseTimer.current);
      dropdownCloseTimer.current = null;
    }
    setOpenDropdown(null);
  }, []);

  useEffect(
    () => () => {
      if (dropdownCloseTimer.current !== null) {
        window.clearTimeout(dropdownCloseTimer.current);
      }
    },
    []
  );

  /** Escape closes the dropdown for keyboard users. */
  useEffect(() => {
    if (!openDropdown) return;
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') closeNavDropdownNow();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openDropdown, closeNavDropdownNow]);

  useEffect(() => {
    setLandingMenuOpen(false);
    closeNavDropdownNow();
  }, [location.pathname, location.hash, closeNavDropdownNow]);

  return (
    <>
      <header
        className={`main-header ${showLandingNav ? 'landing-mode' : ''} ${
          showAuthButtons ? 'auth-mode' : ''
        } ${showLandingNav && hasUserScrolled && scrolled ? 'scrolled' : ''} ${
          openDropdown ? 'has-open-dropdown' : ''
        }`}
      >
        <div className="header-left">
          <div className="brand">
            <Link to="/" className="brand-link">
              <div className="brand-logo-box">
                <img decoding="async" src="/assets/img/header brand logo.webp" alt="Reffconnect Logo" className="brand-logo" />
              </div>
              <div className="brand-name-box">
                <span className="brand-wordmark">REFFCONNECT</span>
              </div>
            </Link>
          </div>

          {showLandingNav && (
            <button
              type="button"
              className="landing-menu-btn"
              onClick={() => setLandingMenuOpen((prev) => !prev)}
              aria-label="Toggle landing menu"
              aria-expanded={landingMenuOpen}
            >
              {React.createElement(IoReorderThreeOutline as any, { size: 22 })}
            </button>
          )}
        </div>

        {showLandingNav && (
          <nav className="landing-header-nav" aria-label="Landing navigation">
            {landingNavItems.map((item) => {
              const menu = item.menu;
              const isActive = location.pathname === item.to;

              if (!menu) {
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`landing-header-link ${isActive ? 'active' : ''}`}
                    onClick={() => trackNavClick(item.label, item.to, 'desktop_nav')}
                  >
                    {item.label}
                  </Link>
                );
              }

              const isOpen = openDropdown === item.label;
              return (
                <div
                  key={item.label}
                  className={`landing-nav-group ${isOpen ? 'is-open' : ''}`}
                  onMouseEnter={() => openNavDropdown(item.label)}
                  onMouseLeave={scheduleCloseNavDropdown}
                >
                  <button
                    type="button"
                    className={`landing-header-link landing-header-link--hasMenu ${isActive ? 'active' : ''}`}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    onFocus={() => openNavDropdown(item.label)}
                    onClick={() => {
                      trackNavClick(item.label, item.to, 'desktop_nav');
                      if (isOpen) {
                        closeNavDropdownNow();
                      } else {
                        openNavDropdown(item.label);
                      }
                    }}
                  >
                    {item.label}
                    <span className="landing-header-link__caret" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </nav>
        )}

        <div className="header-actions">
          {showAuthButtons && (
            <>
              {/* Original auth links — restore when login/signup go live
              <Button href="/login" className="signin">
                Sign In
              </Button>
              <Button href="/signup" className="signup">
                Sign Up
              </Button>
              */}
              <MovingBorderButton
                className="header-mbb signin"
                onClick={() => openLaunchModal('signin')}
                duration={3000}
                glowColor="rgba(255, 255, 255, 0.6)"
                borderRadius="999px"
              >
                Sign In
              </MovingBorderButton>
              <MovingBorderButton
                className="header-mbb signup"
                onClick={() => openLaunchModal('signup')}
                duration={3000}
                glowColor="rgba(245, 158, 11, 0.8)"
                borderRadius="999px"
              >
                Sign Up
              </MovingBorderButton>
            </>
          )}
        </div>

        {/* Full-width mega menu — spans the whole header, shows only the
            options for the hovered nav item. */}
        {showLandingNav && (
          <div
            className="landing-megamenu"
            onMouseEnter={() => openDropdown && openNavDropdown(openDropdown)}
            onMouseLeave={scheduleCloseNavDropdown}
          >
            <div className="landing-megamenu__panel">
              <div
                className={`landing-megamenu__grid landing-megamenu__grid--${openMenuVariant}`}
                role="menu"
                aria-label={`${openDropdown ?? ''} menu`}
              >
                {openMenu?.map((sub) =>
                  openMenuVariant === 'showcase' ? (
                    <Link
                      key={sub.label}
                      to={sub.to}
                      role="menuitem"
                      tabIndex={openDropdown ? 0 : -1}
                      className="landing-megamenu__showcase"
                      onClick={() => {
                        trackNavClick(sub.label, sub.to, 'desktop_nav');
                        closeNavDropdownNow();
                      }}
                    >
                      <span className="landing-megamenu__showcaseVisual">{sub.visual}</span>
                      <span className="landing-megamenu__showcaseTitle">{sub.label}</span>
                      <span className="landing-megamenu__showcaseText">{sub.description}</span>
                    </Link>
                  ) : (
                    <Link
                      key={sub.label}
                      to={sub.to}
                      role="menuitem"
                      tabIndex={openDropdown ? 0 : -1}
                      className="landing-megamenu__card"
                      onClick={() => {
                        trackNavClick(sub.label, sub.to, 'desktop_nav');
                        closeNavDropdownNow();
                      }}
                    >
                      <span className="landing-megamenu__cardVisual">
                        <span className="landing-megamenu__cardIcon" aria-hidden="true">
                          {sub.icon}
                        </span>
                      </span>
                      <span className="landing-megamenu__cardTitle">{sub.label}</span>
                      <span className="landing-megamenu__cardText">{sub.description}</span>
                    </Link>
                  )
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {showLandingNav && landingMenuOpen && (
        <nav className="landing-mobile-nav" aria-label="Landing mobile navigation">
          {landingNavItems.map((item) => {
            const menu = item.menu;
            const isExpanded = mobileExpandedGroup === item.label;
            return (
              <React.Fragment key={`mobile-${item.label}`}>
                {menu ? (
                  <button
                    type="button"
                    className={`landing-mobile-link landing-mobile-link--parent ${isExpanded ? 'is-expanded' : ''}`}
                    onClick={() => setMobileExpandedGroup(isExpanded ? null : item.label)}
                    aria-expanded={isExpanded}
                  >
                    {item.label}
                    <span className="landing-mobile-link__caret" aria-hidden>▾</span>
                  </button>
                ) : (
                  <Link
                    to={item.to}
                    className={`landing-mobile-link ${location.pathname === item.to ? 'active' : ''}`}
                    onClick={() => {
                      trackNavClick(item.label, item.to, 'mobile_nav');
                      setLandingMenuOpen(false);
                      setMobileExpandedGroup(null);
                    }}
                  >
                    {item.label}
                  </Link>
                )}
                {menu && (
                  <div className={`landing-mobile-submenu ${isExpanded ? 'is-open' : ''}`}>
                    <div className="landing-mobile-submenu__inner">
                      {menu.map((sub) => (
                        <Link
                          key={`mobile-${item.label}-${sub.label}`}
                          to={sub.to}
                          className="landing-mobile-link landing-mobile-link--sub"
                          onClick={() => {
                            trackNavClick(sub.label, sub.to, 'mobile_nav');
                            setLandingMenuOpen(false);
                            setMobileExpandedGroup(null);
                          }}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      )}

      {launchModalOpen && (
        <div
          className="launch-notice-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby={subscribeFeedback === 'success' ? 'launch-notice-success-title' : 'launch-notice-title'}
          aria-describedby={
            subscribeFeedback === 'success' ? 'launch-notice-success-details' : 'launch-notice-desc'
          }
          onClick={(e) => {
            if (e.target === e.currentTarget) closeLaunchModal();
          }}
        >
          <div
            key={launchModalAnimKey}
            className={`launch-notice-dialog ${subscribeFeedback === 'success' ? 'launch-notice-dialog--celebrate' : ''}`}
            data-variant={launchModalVariant}
          >
            <button
              type="button"
              className="launch-notice-close"
              onClick={closeLaunchModal}
              aria-label="Close"
            >
              ×
            </button>
            {subscribeFeedback === 'success' ? (
              <div key={successAnimKey} className="launch-notice-success-panel" role="status" aria-live="polite">
                {/* Previous simple success version kept for quick rollback
                <h2 id="launch-notice-success-title" className="launch-notice-title launch-notice-title--success">
                  Thank you for subscribing
                </h2>
                <p id="launch-notice-success-details" className="launch-notice-success-text">
                  {launchCopy.successMessage}
                </p>
                <Button type="button" className="launch-notice-dismiss" onClick={closeLaunchModal}>
                  Close
                </Button>
                */}
                <div className="launch-notice-success-hero">
                  <div className="launch-notice-success-glow" aria-hidden="true" />
                  <div className="launch-notice-success-badge" aria-hidden="true">
                    <IoCheckmark className="launch-notice-success-check-icon" size={28} />
                  </div>
                  <span className="launch-notice-success-chip">{successStory.badge}</span>
                  <h2 id="launch-notice-success-title" className="launch-notice-title launch-notice-title--success">
                    {successStory.headline}
                  </h2>
                  <p className="launch-notice-success-subhead">{successStory.subhead}</p>
                </div>
                <div id="launch-notice-success-details" className="launch-notice-success-body">
                  {successStory.bodyLines.map((line, i) => (
                    <p key={`success-line-${i}`} className="launch-notice-success-para">
                      {line}
                    </p>
                  ))}
                  <div className="launch-notice-success-perks">
                    <p className="launch-notice-success-perks-title">{successStory.perksTitle}</p>
                    <ul className="launch-notice-success-perk-list">
                      {successStory.perks.map((perk) => (
                        <li key={perk} className="launch-notice-success-perk-item">
                          <span className="launch-notice-success-perk-dot" aria-hidden="true" />
                          {perk}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="launch-notice-success-invite">{successStory.invite}</p>
                  <div className="launch-notice-share-box">
                    <p className="launch-notice-share-title">Share Reffconnect</p>
                    <a
                      className="launch-notice-share-url"
                      href={REFFCONNECT_WEBSITE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      www.reffconnect.com
                    </a>
                    <div className="launch-notice-share-actions">
                      <a href={whatsappShareUrl} target="_blank" rel="noopener noreferrer" className="launch-notice-share-btn">
                        WhatsApp
                      </a>
                      <a href={linkedInShareUrl} target="_blank" rel="noopener noreferrer" className="launch-notice-share-btn">
                        LinkedIn
                      </a>
                      <a
                        href="https://www.instagram.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="launch-notice-share-btn"
                      >
                        Instagram
                      </a>
                    </div>
                  </div>
                  <div className="launch-notice-follow-box">
                    <p className="launch-notice-follow-title">Follow Reffconnect for launch updates</p>
                    <p className="launch-notice-follow-text">
                      We will post the official launch announcement on our socials.
                    </p>
                    <div className="launch-notice-follow-actions">
                      <a
                        href="https://www.instagram.com/reffconnect/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="launch-notice-follow-btn"
                      >
                        Instagram
                      </a>
                      <a
                        href="https://www.linkedin.com/company/reffconnect/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="launch-notice-follow-btn"
                      >
                        LinkedIn
                      </a>
                    </div>
                  </div>
                  <footer className="launch-notice-success-signoff">
                    <p className="launch-notice-success-thanks">{successStory.signOff}</p>
                    <p className="launch-notice-success-team">{successStory.team}</p>
                  </footer>
                </div>
                <button type="button" className="custom-btn launch-notice-dismiss" onClick={closeLaunchModal}>
                  Awesome—close this
                </button>
              </div>
            ) : (
              <>
                <p className="launch-notice-context" aria-hidden="true">
                  {launchModalVariant === 'signin' ? 'Sign in' : 'Sign up'}
                </p>
                <h2 id="launch-notice-title" className="launch-notice-title">
                  {launchCopy.title}
                </h2>
                <p id="launch-notice-desc" className="launch-notice-text">
                  {launchCopy.message}
                </p>
                <form className="launch-notice-form" onSubmit={handleLaunchSubscribe} noValidate>
                  <label htmlFor="launch-notice-email" className="launch-notice-label">
                    {launchCopy.formLabel}
                  </label>
                  <input
                    id="launch-notice-email"
                    name="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    className="launch-notice-input"
                    placeholder={launchCopy.inputPlaceholder}
                    value={subscribeEmail}
                    onChange={(e) => {
                      if (!subscribeEmail) {
                        trackFormStart({ form_name: 'waitlist_popup', page_path: window.location.pathname });
                      }
                      setSubscribeEmail(e.target.value);
                      setSubscribeSubmitError(null);
                      if (subscribeFeedback !== 'idle') {
                        setSubscribeFeedback('idle');
                        setSubscribeInvalidField(null);
                      }
                    }}
                    disabled={subscribeSubmitting}
                  />
                  <label htmlFor="launch-notice-role" className="launch-notice-label">
                    You are a
                  </label>
                  <select
                    id="launch-notice-role"
                    name="role"
                    className="launch-notice-input launch-notice-select"
                    value={waitlistRole}
                    onChange={(e) => {
                      const v = e.target.value as '' | 'professional' | 'jobseeker';
                      setWaitlistRole(v);
                      setSubscribeSubmitError(null);
                      if (v === 'jobseeker') {
                        setSubscribeCompany('');
                      }
                      if (subscribeFeedback !== 'idle') {
                        setSubscribeFeedback('idle');
                        setSubscribeInvalidField(null);
                      }
                    }}
                    disabled={subscribeSubmitting}
                  >
                    <option value="">Select one</option>
                    <option value="professional">Working professional</option>
                    <option value="jobseeker">Jobseeker</option>
                  </select>
                  {waitlistRole === 'professional' && (
                    <>
                      <label htmlFor="launch-notice-company" className="launch-notice-label">
                        Company name <span className="launch-notice-required" aria-hidden="true">*</span>
                      </label>
                      <input
                        id="launch-notice-company"
                        name="company"
                        type="text"
                        autoComplete="organization"
                        className="launch-notice-input"
                        placeholder="e.g. Acme Technologies"
                        value={subscribeCompany}
                        onChange={(e) => {
                          setSubscribeCompany(e.target.value);
                          setSubscribeSubmitError(null);
                          if (subscribeFeedback !== 'idle') {
                            setSubscribeFeedback('idle');
                            setSubscribeInvalidField(null);
                          }
                        }}
                        disabled={subscribeSubmitting}
                      />
                    </>
                  )}
                  {subscribeFeedback === 'invalid' && (
                    <p className="launch-notice-hint launch-notice-hint--error" role="alert">
                      {subscribeInvalidField === 'email'
                        ? launchCopy.invalidMessage
                        : subscribeInvalidField === 'role'
                          ? 'Please choose Working professional or Jobseeker.'
                          : subscribeInvalidField === 'company'
                            ? 'Please enter your company name.'
                          : null}
                    </p>
                  )}
                  {subscribeSubmitError && (
                    <p className="launch-notice-hint launch-notice-hint--error" role="alert">
                      {subscribeSubmitError}
                    </p>
                  )}
                  <Button
                    type="submit"
                    className="launch-notice-submit"
                    disabled={subscribeSubmitting}
                    aria-busy={subscribeSubmitting}
                    onClick={() =>
                      trackWaitlistNotifyButtonClick({
                        variant: launchModalVariant,
                        submit_label: launchCopy.submitLabel,
                      })
                    }
                  >
                    {subscribeSubmitting ? 'Saving…' : launchCopy.submitLabel}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
